import React from "react";

import "./ModeToggle.scss";

export type AppMode = "draw" | "weave";

interface ModeToggleProps {
  mode: AppMode;
  onChange: (mode: AppMode) => void;
  drawLabel: string;
  drawDesc: string;
  weaveLabel: string;
  weaveDesc: string;
  isDark?: boolean;
  size?: "default" | "large";
}

// ⚡ Draw Art: Energy, Sparks, Lightning
const DrawArt: React.FC = () => (
  <svg
    className="mode-toggle__art mode-toggle__art--draw"
    viewBox="0 0 160 46"
    fill="none"
    aria-hidden="true"
  >
    {/* Speed lines shooting past */}
    <line
      x1="10"
      y1="12"
      x2="150"
      y2="12"
      stroke="currentColor"
      strokeWidth="1"
      className="mt-speed-line mt-speed-line--1"
    />
    <line
      x1="0"
      y1="34"
      x2="160"
      y2="34"
      stroke="currentColor"
      strokeWidth="1.5"
      className="mt-speed-line mt-speed-line--2"
    />
    <line
      x1="20"
      y1="24"
      x2="140"
      y2="24"
      stroke="currentColor"
      strokeWidth="0.5"
      className="mt-speed-line mt-speed-line--3"
    />

    {/* Sparks flying */}
    <circle
      cx="30"
      cy="15"
      r="1.5"
      fill="currentColor"
      className="mt-spark mt-spark--1"
    />
    <circle
      cx="130"
      cy="35"
      r="2"
      fill="currentColor"
      className="mt-spark mt-spark--2"
    />
    <circle
      cx="80"
      cy="8"
      r="1"
      fill="currentColor"
      className="mt-spark mt-spark--3"
    />
    <circle
      cx="110"
      cy="20"
      r="1.5"
      fill="currentColor"
      className="mt-spark mt-spark--4"
    />
  </svg>
);

// 🌌 Weave Art: Neural Network, Constellation, Magic
const WeaveArt: React.FC = () => (
  <svg
    className="mode-toggle__art mode-toggle__art--weave"
    viewBox="0 0 160 46"
    fill="none"
    aria-hidden="true"
  >
    <defs>
      <filter id="glow-weave" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="0.8" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    {/* Network connections */}
    <path
      d="M25 23 L55 12 L85 32 L125 15 L145 28"
      stroke="currentColor"
      strokeWidth="1"
      className="mt-net-line"
    />
    <path
      d="M55 12 L80 18 L85 32"
      stroke="currentColor"
      strokeWidth="0.5"
      className="mt-net-line mt-net-line--alt"
    />
    <path
      d="M25 23 L40 35 L85 32"
      stroke="currentColor"
      strokeWidth="0.5"
      className="mt-net-line mt-net-line--alt2"
    />

    {/* Pulsing nodes */}
    <g filter="url(#glow-weave)">
      <circle
        cx="25"
        cy="23"
        r="2.5"
        fill="currentColor"
        className="mt-node mt-node--1"
      />
      <circle
        cx="55"
        cy="12"
        r="3"
        fill="currentColor"
        className="mt-node mt-node--2"
      />
      <circle
        cx="85"
        cy="32"
        r="3.5"
        fill="currentColor"
        className="mt-node mt-node--3"
      />
      <circle
        cx="125"
        cy="15"
        r="2.5"
        fill="currentColor"
        className="mt-node mt-node--4"
      />
      <circle
        cx="145"
        cy="28"
        r="2"
        fill="currentColor"
        className="mt-node mt-node--5"
      />
      <circle
        cx="80"
        cy="18"
        r="1.5"
        fill="currentColor"
        className="mt-node mt-node--6"
      />
      <circle
        cx="40"
        cy="35"
        r="1.5"
        fill="currentColor"
        className="mt-node mt-node--7"
      />
    </g>

    {/* Rotating magic sparkles */}
    <g className="mt-magic-stars">
      <path
        d="M110 5 L112 10 L117 12 L112 14 L110 19 L108 14 L103 12 L108 10 Z"
        fill="currentColor"
        className="mt-magic-star mt-magic-star--1"
      />
      <path
        d="M40 5 L41 8 L44 9 L41 10 L40 13 L39 10 L36 9 L39 8 Z"
        fill="currentColor"
        className="mt-magic-star mt-magic-star--2"
      />
    </g>
  </svg>
);

export const ModeToggle: React.FC<ModeToggleProps> = ({
  mode,
  onChange,
  drawLabel,
  drawDesc,
  weaveLabel,
  weaveDesc,
  isDark = false,
  size = "default",
}) => {
  const isWeave = mode === "weave";
  const sizeClass = size === "large" ? " mode-toggle--large" : "";

  return (
    <div
      className={`mode-toggle${sizeClass}${isDark ? " mode-toggle--dark" : ""}${
        isWeave ? " mode-toggle--weave" : ""
      }`}
      role="radiogroup"
      aria-label="Mode selection"
    >
      {/* Sliding highlight background */}
      <div
        className={`mode-toggle__highlight${
          isWeave ? " mode-toggle__highlight--weave" : ""
        }`}
        aria-hidden="true"
      >
        <div className="mode-toggle__shimmer" />
      </div>

      {/* Draw option */}
      <button
        type="button"
        role="radio"
        aria-checked={!isWeave}
        className={`mode-toggle__option mode-toggle__option--draw ${
          !isWeave ? "mode-toggle__option--active" : ""
        }`}
        onClick={() => onChange("draw")}
      >
        <div className="mode-toggle__hover-bg" />
        <DrawArt />
        <span className="material-symbols-outlined mode-toggle__option-icon">
          bolt
        </span>
        <span className="mode-toggle__text">
          <span className="mode-toggle__name">{drawLabel}</span>
          <span className="mode-toggle__desc">{drawDesc}</span>
        </span>
      </button>

      {/* Weave option */}
      <button
        type="button"
        role="radio"
        aria-checked={isWeave}
        className={`mode-toggle__option mode-toggle__option--weave ${
          isWeave ? "mode-toggle__option--active" : ""
        }`}
        onClick={() => onChange("weave")}
      >
        <div className="mode-toggle__hover-bg" />
        <WeaveArt />
        <span className="material-symbols-outlined mode-toggle__option-icon">
          hub
        </span>
        <span className="mode-toggle__text">
          <span className="mode-toggle__name">{weaveLabel}</span>
          <span className="mode-toggle__desc">{weaveDesc}</span>
        </span>
      </button>
    </div>
  );
};
